import type { SpinBoundaryStartInput, SpinBoundaryEndInput } from "@slot/client-engine";
import type { ReplayContext } from "@slot/client-engine";
import type { Log, PresentationTaskRunner } from "@slot/presentation";

export class SpinBoundaryPacing {
  private readonly log: Log;
  private readonly runner: PresentationTaskRunner;

  /**
   * Per-spin settle-beat (ms) applied at each spin boundary when animations
   * are NOT skipped (i.e. normal mode; turbo/super_turbo skip via
   * shouldSkipAnimations). Defaults to 200 so existing mechanics (cascade)
   * are unchanged. A mechanic whose own reveal animation already provides the
   * pacing (e.g. reels) passes 0 to opt out — 0 skips the beat entirely.
   */
  private readonly boundaryPaceMilliseconds: number;

  private currentSpinBoundaryId: string | null = null;

  public constructor(input: { log: Log; runner: PresentationTaskRunner; boundaryPaceMilliseconds?: number }) {
    this.log = input.log;
    this.runner = input.runner;
    this.boundaryPaceMilliseconds = input.boundaryPaceMilliseconds ?? 200;
  }

  public async onSpinBoundaryStart(input: SpinBoundaryStartInput, context: ReplayContext): Promise<void> {
    this.currentSpinBoundaryId = input.spinBoundaryIdentifier;

    this.log(`[SpinBoundaryPacing] start id=${input.spinBoundaryIdentifier} mode=${input.spinModeIdentifier ?? "?"}`);

    await this.beat(context);
  }

  public async onSpinBoundaryEnd(input: SpinBoundaryEndInput, context: ReplayContext): Promise<void> {
    if (this.currentSpinBoundaryId && input.spinBoundaryIdentifier !== this.currentSpinBoundaryId) {
      this.log(`[SpinBoundaryPacing] end mismatch expected=${this.currentSpinBoundaryId} got=${input.spinBoundaryIdentifier}`);
    }

    this.log(`[SpinBoundaryPacing] end id=${input.spinBoundaryIdentifier} spinWin=${input.spinWinAmount ?? "?"}`);

    this.currentSpinBoundaryId = null;

    await this.beat(context);
  }

  /** The settle beat on the round's clock: it scales with the speed mode and freezes with the dev pause. */
  private async beat(context: ReplayContext): Promise<void> {
    if (context.configuration.shouldSkipAnimations || this.boundaryPaceMilliseconds <= 0) return;
    await this.runner.sleep(this.boundaryPaceMilliseconds / 1000);
  }
}
